import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { CoreSessionStorage } from '../../../storage/session/CoreSessionStorage';
import { getCurrentLanguage, getIsLanguageRTL } from '../../../utils/language';
import { fetchPlayerConfigAction } from '../../apiActions/fetchPlayerConfigAction';

import { type ISetLanguagePayload, type ISettingsInitialState, StreamQuality, type VolumeChannelType } from './types';

const initialState: ISettingsInitialState = {
  isInFullscreen: false,
  isGameVisible: true,
  isSecondBetBoardToggle: true,
  isAnimationToggle: false,
  stream: {
    isEnabled: true,
    isEnabledBySystem: true,
    isInClassicView: false,
    quality: StreamQuality.Auto,
    qualityLimit: 0, //StreamQuality.High
    streamLatency: 1000,
  },
  volume: {
    restored: false,
    master: {
      value: 0,
      temporaryValue: null,
      previousValue: CoreSessionStorage.getLastPositiveMasterVolume() ?? 1,
    },
    channels: {},
    uiChannel: null,
  },
  language: {},
  showOtherMessages: true,
};

export const settingsSlice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setIsInFullscreen: (state, action: PayloadAction<boolean>) => {
      state.isInFullscreen = action.payload;
    },
    /* Stream */
    // Listened by settingSyncMiddleware
    setStreamEnabled: (state, action: PayloadAction<boolean>) => {
      state.stream.isEnabled = action.payload;
    },
    // Flag used for managing stream on inactivity
    setStreamEnabledBySystem: (state, action: PayloadAction<boolean>) => {
      state.stream.isEnabledBySystem = action.payload;
    },
    setStreamClassicView: (state, action: PayloadAction<boolean>) => {
      state.stream.isInClassicView = action.payload;
      // Switch back to default stream when classic view gets disabled
      if (!action.payload) {
        state.stream.isEnabled = true;
      }
    },
    setStreamQualityLimit: (state, action: PayloadAction<number>) => {
      state.stream.qualityLimit = action.payload;
    },
    setStreamQuality: (state, action: PayloadAction<StreamQuality>) => {
      state.stream.quality = action.payload;
    },
    setStreamLatency: (state, action: PayloadAction<number>) => {
      state.stream.streamLatency = action.payload;
    },
    isAnimationToggle: (state) => {
      state.isAnimationToggle = !state.isAnimationToggle;
    },
    isSecondBetBoardToggle: (state) => {
      state.isSecondBetBoardToggle = !state.isSecondBetBoardToggle;
    },

    /* Chat */
    setShowOtherMessages: (state, action: PayloadAction<boolean>) => {
      state.showOtherMessages = action.payload;
    },

    /* Volume */
    setGameVolumeChannels: (state, action: PayloadAction<string[]>) => {
      const newChannels = action.payload.reduce(
        (acc, channel) => {
          if (state.volume.channels[channel]) return acc;
          acc[channel] = {
            value: 0,
            temporaryValue: null,
            previousValue: 1,
          };
          return acc;
        },
        {} as { [key: string]: VolumeChannelType }
      );
      state.volume.channels = {
        ...state.volume.channels,
        ...newChannels,
      };
    },

    setMasterVolumeOnly: (state, action: PayloadAction<number>) => {
      state.volume.restored = true;
      const newVolume = action.payload;
      state.volume.master.value = newVolume;

      // This is needed for restoring to positive value after unmute
      if (newVolume) {
        state.volume.master.previousValue = newVolume;
        CoreSessionStorage.setLastPositiveMasterVolume(newVolume);
      }

      state.volume.master.temporaryValue = null;
      CoreSessionStorage.setLastMasterVolume(newVolume);
    },

    // Listened by settingSyncMiddleware
    setMasterVolume: (state, action: PayloadAction<number>) => {
      state.volume.restored = true;
      const newVolume = action.payload;
      state.volume.master.value = newVolume;

      //we need to check if all chanel are off we should turn them on
      const noVolumeControl = state.volume.noVolumeControl;
      if (noVolumeControl) {
        Object.keys(state.volume.channels).forEach((key) => {
          //todo: make sure studio chanel is defined in core
          if (!state.stream.isEnabled && key === 'studio' && action.payload > 0) return;

          state.volume.channels[key].value = action.payload;
          state.volume.channels[key].previousValue = action.payload;
        });
      } else {
        const chanelNamesWithVolume = Object.keys(state.volume.channels).filter((chanelName) => {
          return state.volume.channels[chanelName].value > 0;
        });

        if (chanelNamesWithVolume.length === 0) {
          Object.keys(state.volume.channels).forEach((key) => {
            //todo: make sure studio chanel is defined in core
            if (noVolumeControl && !state.stream.isEnabled && key === 'studio') return;

            state.volume.channels[key].value = 1;
            state.volume.channels[key].previousValue = 1;
          });
        }
      }

      // Used for restoring master volume after unmute
      if (newVolume) {
        state.volume.master.previousValue = newVolume;
        CoreSessionStorage.setLastPositiveMasterVolume(newVolume);
      }

      state.volume.master.temporaryValue = null;
      CoreSessionStorage.setLastMasterVolume(newVolume);
    },
    setMasterVolumeTemporary: (state, action: PayloadAction<number | null>) => {
      state.volume.master.temporaryValue = action.payload;
    },

    setAllVolumes: (state, action: PayloadAction<number>) => {
      state.volume.restored = true;
      Object.keys(state.volume.channels).forEach((key) => {
        state.volume.channels[key].value = action.payload;
      });
    },

    // Listened by settingSyncMiddleware
    setVolume: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
    ) => setVolumeValue(state, action),

    // Avoid redundant savePlayerConfigGeneric API call on iOS devices in VolumeRestorer
    setVolumeIOSToggle: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
    ) => setVolumeValue(state, action),

    setVolumeTemporary: (
      state,
      action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number | null }>
    ) => {
      const { channel, value } = action.payload;
      if (state.volume.channels[channel]) {
        state.volume.channels[channel].temporaryValue = value;
      } else {
        throw new Error(`Volume channel ${channel} does not exist`);
      }
    },
    setUiVolumeChannel: (state, action: PayloadAction<string | null>) => {
      state.volume.uiChannel = action.payload;
    },
    setNoVolumeControl: (state, action: PayloadAction<boolean>) => {
      state.volume.noVolumeControl = action.payload;
    },
    /* Language */
    setLanguage: (state, action: PayloadAction<ISetLanguagePayload>) => {
      state.language.current = getCurrentLanguage(action.payload.languages, action.payload.sessionLanguage);
      state.language.isRTL = getIsLanguageRTL(state.language.current);
    },
    setIsGameVisible: (state, action: PayloadAction<boolean>) => {
      state.isGameVisible = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlayerConfigAction.fulfilled, (state, action) => {
      state.stream.isEnabled = action.payload.videoStream;
      // TODO: default channels should be mapped via game config
      if (state.volume.channels['studio']) {
        state.volume.channels['studio'].value = action.payload.videoVolume;
        state.volume.channels['studio'].previousValue = action.payload.videoVolume;
      }
      if (state.volume.channels['game']) {
        state.volume.channels['game'].value = action.payload.gameEffectsVolume;
        state.volume.channels['game'].previousValue = action.payload.gameEffectsVolume;
      }
      state.showOtherMessages = action.payload.showOtherMessages;
      state.isAnimationToggle = action.payload.hfAnimationEnabled;
      state.isSecondBetBoardToggle = action.payload.isSecondBetboardEnabled; 
    });
  },
});

// Utils
const setVolumeValue = (
  state: any,
  action: PayloadAction<{ channel: keyof (typeof initialState)['volume']['channels']; value: number }>
): void => {
  const { channel, value } = action.payload;
  state.volume.restored = true;
  if (state.volume.channels[channel]) {
    state.volume.channels[channel].value = value;

    //we need to check if all channels are off we have to turn of master channel too
    if (value === 0) {
      const chanelNamesWithVolume = Object.keys(state.volume.channels).filter(
        (chanelName) => state.volume.channels[chanelName].value > 0
      );
      if (chanelNamesWithVolume.length === 0) {
        state.volume.master.value = 0;
      }
    }

    if (value) {
      state.volume.channels[channel].previousValue = value;
    }
    state.volume.channels[channel].temporaryValue = null;
  } else {
    throw new Error(`Volume channel ${channel} does not exist`);
  }
};

export const settingsActions = settingsSlice.actions;
